<template>
  <div>
    <h3 v-if="title">{{ title }}</h3>

    <ul class="nav nav-tabs nav-tabs-custom mb-4" role="tablist">
      <li class="nav-item">
        <a
            class="nav-link"
            :class="{ active: activeTab === 'excel' }"
            @click.prevent="setActiveTab('excel')"
            href="#"
            role="tab"
        >
          <i class="bx bx-spreadsheet me-1"></i> Import z Excel
        </a>
      </li>
      <li class="nav-item">
        <a
            class="nav-link"
            :class="{ active: activeTab === 'source' }"
            @click.prevent="setActiveTab('source')"
            href="#"
            role="tab"
        >
          <i class="bx bx-building-house me-1"></i> Import z danych aptek
        </a>
      </li>
    </ul>

    <div class="tab-content p-3 text-muted">

      <div
          class="tab-pane"
          :class="{ active: activeTab === 'excel' }"
          id="excel-import"
          role="tabpanel"
          v-show="activeTab === 'excel'"
      >
        <div class="row">
          <CategoryTreeForm ref="categoryTreeForm"
          @onSave="applyBasicFormData"
          />
        </div>
        <div class="row">
          <CategoryTreeExcelImportForm
              ref="categoryTreeExcelImportForm"
              @onPreferredIdentifierChange="setPreferredIdentifierForCategoryTree"
              @onSave="applyColumnMappingData"
          />
        </div>

        <div class="mt-3">
          <button class="btn btn-primary" @click="saveData">
            Importuj z Excela
          </button>
        </div>
      </div>

      <div
          class="tab-pane"
          :class="{ active: activeTab === 'source' }"
          id="source-import"
          role="tabpanel"
          v-show="activeTab === 'source'"
      >
        <div class="row">
          <div class="col-md-6 col-lg-4">
            <div class="mb-3">
              <label for="sourcePharmacyId" class="form-label font-size-14 text-dark fw-bold">
                ID Źródłowej Apteki
              </label>
              <div class="input-group has-validation">
                <span class="input-group-text"><i class="bx bx-id-card"></i></span>
                <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': sourcePharmacyIdError }"
                    id="sourcePharmacyId"
                    v-model="sourcePharmacyId"
                    @input="validateSourcePharmacyId"
                    placeholder="Np. 123456"
                    maxlength="6"
                />
                <div v-if="sourcePharmacyIdError" class="invalid-feedback">
                  {{ sourcePharmacyIdError }}
                </div>
              </div>
              <small class="text-muted">
                Wprowadź identyfikator składający się maksymalnie z 6 cyfr.
              </small>
            </div>
          </div>
        </div>

        <div class="mt-3">
          <button
              class="btn btn-info text-white"
              @click="saveDataSource"
              :disabled="!!sourcePharmacyIdError || !sourcePharmacyId"
          >
            Importuj dane
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import CategoryTreeExcelImportForm from './CategoryTreeExcelImportForm.vue';
import CategoryTreeForm from './CategoryTreeForm.vue';
import {apiRequestsHelper} from '@/state/pinia/apiRequestHelper';
import Swal from 'sweetalert2';
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

/**
 * Komponent asystenta importu drzewa kategorii.
 * Obsługuje dwa tryby: import z pliku Excel oraz import na podstawie ID źródłowego.
 */
export default defineComponent({
  name: 'CategoryTreeImportAssistant',
  components: {CategoryTreeExcelImportForm, CategoryTreeForm},
  props: {
    title: String,
  },
  emits: [],
  data() {
    return {
      // Oryginalne dane
      baseFormData: null,
      mappingData: null,
      importFile: null,
      preferredIdentifier: null,

      // Nowe dane do obsługi zakładek i walidacji
      activeTab: 'excel', // 'excel' | 'source'
      sourcePharmacyId: '',
      sourcePharmacyIdError: null,
    };
  },
  computed: {
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonAppModule() {
      return useCommonAppModule();
    },
    lazyloader() {
      return useLazyLoaderModule();
    },
  },
  methods: {
    /**
     * Zmienia aktywną zakładkę.
     * @param {string} tabName - Nazwa zakładki ('excel' lub 'source')
     */
    setActiveTab(tabName) {
      this.activeTab = tabName;
    },

    /**
     * Waliduje ID źródłowej apteki.
     * Zasady: Tylko cyfry, max 6 znaków.
     */
    validateSourcePharmacyId() {
      const value = this.sourcePharmacyId;

      // Sprawdź czy to same cyfry
      const isNumeric = /^\d*$/.test(value);

      if (!isNumeric) {
        this.sourcePharmacyIdError = 'Pole może zawierać tylko cyfry.';
        // Opcjonalnie: wyczyść znaki niebędące cyframi
        this.sourcePharmacyId = value.replace(/\D/g, '');
        return false;
      }

      if (value.length > 6) {
        this.sourcePharmacyIdError = 'Maksymalna długość to 6 znaków.';
        return false;
      }

      this.sourcePharmacyIdError = null;
      return true;
    },

    /**
     * Obsługa importu z danych źródłowych (Nowa metoda).
     * Symuluje walidację i wysłanie danych.
     */
    saveDataSource() {
      if (!this.validateSourcePharmacyId() || !this.sourcePharmacyId) {
        Swal.fire({
          title: 'Błąd walidacji',
          text: 'Popraw ID apteki źródłowej.',
          icon: 'warning',
          customClass: {container: 'swal-highest'},
        });
        return;
      }

      Swal.fire({
        title: 'Uwaga',
        text: 'Import danych z apteki źródłowej może potrwać nawet do kilku minut. Czy na pewno chcesz kontynuować?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, kontynuuj',
        cancelButtonText: 'Nie, anuluj',
        customClass: {container: 'swal-highest'},
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        this.lazyloader.setDisplayLazyLoader(true);
        console.log('Rozpoczynam import ze źródła dla ID apteki:', this.sourcePharmacyId);
        this.commonAppModule.httpImportCategoriesFromSourceDrugstore({drugstoreClientId: this.sourcePharmacyId})
      })


    },

    // --- Poniżej oryginalne metody bez zmian logicznych ---

    setPreferredIdentifierForCategoryTree(val) {
      this.preferredIdentifier = val;
    },

    applyBasicFormData(data){
      this.baseFormData = data;
    },
    applyColumnMappingData(data){
      if(!data || data.items.length === 0) return;
      this.mappingData = data.items;
    },
    applyExcelForm() {
      // Wywołanie metod z refów (zachowana logika)
      const form1Valid = this.$refs.categoryTreeForm.saveData();
      if (!form1Valid) return false;



      const form2Valid = this.$refs.categoryTreeExcelImportForm.saveData();
      if (!form2Valid) return false;


      return true;
    },

    saveData() {
      if (!this.$refs.categoryTreeForm || !this.$refs.categoryTreeExcelImportForm) {
        Swal.fire({
          title: 'Błąd!',
          text: 'Wystąpił błąd wewnętrzny formularza.',
          icon: 'error',
          customClass: {container: 'swal-highest'},
        });
        return;
      }
      const excelValid = this.applyExcelForm();
      console.log('excelValid', excelValid);
      if (!excelValid) {
        Swal.fire({
          title: 'Dane niepoprawne!',
          text: 'Uzupełnij dane pliku oraz mapowanie kolumn.',
          icon: 'error',
          customClass: {container: 'swal-highest'},
        });
        return;
      }

      this.baseFormData['preferredIdentifier'] = this.preferredIdentifier;

      const payload = {
        details: this.baseFormData,
        productsInCategory: this.mappingData,
      };

      this.lazyloader.setDisplayLazyLoader(true);

      this.commonAppModule.httpAddCategoryTreeParsedFromFile(payload)
    },
  },
});
</script>

<style lang="scss" scoped>
/* Dodatkowe style dla zakładek, aby wyglądały spójnie z resztą aplikacji */
.nav-tabs-custom {
  border-bottom: 2px solid #eff2f7;

  .nav-item .nav-link {
    border: none;
    font-weight: 500;
    color: #495057;

    &.active {
      color: #556ee6; // Kolor główny (można podmienić na zmienną SCSS)
      background-color: transparent;
      border-bottom: 2px solid #556ee6;
    }

    &:hover {
      color: #556ee6;
    }
  }
}
</style>